import React, { useEffect, useState, useContext, useMemo } from 'react';
import { useAccount, usePublicClient, useWaitForTransactionReceipt, useWalletClient } from 'wagmi';
import Web3 from 'web3';

import { MobileContext } from 'context';
import { GetPredictionContractAddr } from 'context/url_based_context';
import { useGameDetails, useHistoricRound, useIsClaimable_historyRound } from 'utils/Hooks';
import { NormalizeNumber } from 'utils/MetamaskSuite';
import { ToastErrorTx, ToastProgressTx, ToastSuccessTx } from 'utils/Toasts/transaction';
import { availablePositions, ABI } from 'const';
import { NetworkCurrencySymbol, FiatCurrencySymbol } from 'components/CurrencySymbols';
import { UsingDevKey } from 'utils/Dev';
import { ReactComponent as BullIcon } from 'assets/icons/BullIconNew.svg';
import { ReactComponent as BearIcon } from 'assets/icons/BearIcon.svg';
import { ReactComponent as CrossIcon } from 'assets/icons/CrossIcon.svg';
import { ReactComponent as QuestionmarkIcon } from 'assets/icons/Questionmark.svg';
import { ReactComponent as BullIconHalf } from 'assets/icons/BullIconHalf.svg';
import { ReactComponent as BearIconHalf } from 'assets/icons/BearIconHalf.svg';
import { Link } from 'react-router';
import './styles.scss';


function HistoryRound(props) {
  const wagerDecimalsToShow = 4;
  const isMobile = useContext(MobileContext);

  const gameDetails = useGameDetails();
  const round = useHistoricRound(props.roundID);
  const isClaimable = useIsClaimable_historyRound(props.roundID);

  const [alreadyClaimed, SetAlreadyClaimed] = useState(props.claimed);
  const detailsURL = useMemo(GetDetailsURL, [gameDetails, round]);

  const walletClient = useWalletClient({ chainId: gameDetails.wantedChain.id });
  const publicClient = usePublicClient();
  const account = useAccount();

  const [betTxHash, SetBetTxHash] = useState(null);
  const [formattedError, SetFormattedError] = useState('');

  const {
    isSuccess: isTxConfirmed_hook, failureReason: txFailReason
  } = useWaitForTransactionReceipt({ hash: betTxHash, confirmations: 1 });


  useEffect(() => {
    DetermineTxStatusFromHooks()
  }, [isTxConfirmed_hook, betTxHash, txFailReason]);

  useEffect(() => {
    HandleTxError();
  }, [formattedError, betTxHash]);


  function DetermineTxStatusFromHooks() {
    if (isTxConfirmed_hook && betTxHash) {
      console.info(`TX CONFIRMED by hook`);
      ShowTxSuccessToast(betTxHash);
    } else if (txFailReason) {
      /*
      for (const [key, value] of Object.entries(txFailReason)) {
        console.log({ [key]: value });
      }
      */
      console.info(`TX REVERTED according to hook`);
      let reason = 'unknown error';
      const matches = txFailReason.details.match(/:(.*): 0x/);
      if (matches && matches[1])
        reason = matches[1].trim();
      else
        reason = txFailReason.shortMessage.slice(0, 40);
      SetFormattedError(`${reason}`);
    }
  }

  function ShowTxPendingToast() {
    ToastProgressTx();
  }

  function HandleTxError() {
    if (formattedError) {
      ToastErrorTx(formattedError, betTxHash);
      SetBetTxHash(null); // stop useWaitForTransactionReceipt hook from further updates
      SetFormattedError('');
    }
  }

  function ShowTxSuccessToast(_txHash) {
    if (!_txHash)
      throw new SyntaxError(`missing transaction hash when trying to show success toast`);
    ToastSuccessTx(_txHash);
    SetBetTxHash(null); // stop useWaitForTransactionReceipt hook from further updates
    SetAlreadyClaimed(true);
  }


  useEffect(() => SetAlreadyClaimed(props.claimed)
    , [props.claimed]);

  function NotFullyLoaded() {
    return (!account?.address || !props.roundID)
  }

  function RoundNotEnded() {
    if (!round) return true;
    const roundStillInProgress = (round.closeTimestamp > (Date.now() / 1000).toFixed());
    return roundStillInProgress;
  }


  async function Claim() {
    if (NotFullyLoaded()) return;
    if (UsingDevKey())
      alert('You are in debug mode');

    let simulationResult = null;
    try {
      simulationResult = await publicClient.simulateContract({
        address: GetPredictionContractAddr(gameDetails),
        abi: ABI.prediction,
        functionName: 'user_Claim',
        args: [[props.roundID]],
        account: account.address,
        value: 0n,
      });
    } catch (e) {
      if (e?.shortMessage) {
        ToastErrorTx(e.shortMessage);
      }
      return;
    }

    let txHash = '';
    ShowTxPendingToast();
    try {
      txHash = await walletClient.data.writeContract(simulationResult.request);
      SetBetTxHash(txHash);
    } catch (e) {
      if (e?.shortMessage) {
        SetFormattedError(e.shortMessage);
      } else {
        SetFormattedError(String(e).slice(0, 40));
      }
      SetBetTxHash(null);
      return;
    }
  }


  function GetRewardRate() {
    if (!round || !round.rewardBaseCalAmount)
      return 0;
    else if (round.canceled || round.lockPrice == 0 || round.closePrice == 0)
      return 1;
    else
      return (round.rewardAmount / round.rewardBaseCalAmount); // net reward amount
    //return (Number(round.bullAmount) + Number(round.bearAmount)) / round.rewardBaseCalAmount; // gross reward amount
  }

  function GetDetailsURL() {
    return ('/history/' + gameDetails.chainName.toLowerCase() + '/'
      + gameDetails.tickerSymbol.toLowerCase() + `/round/${round.epoch}`);
  }



  /* === JSX methods below this line; used for drawing page layout === */

  const roundNumberM = useMemo(RoundNumber, [detailsURL, props.roundID]);
  function RoundNumber() {
    return (<Link to={detailsURL} className='ThemeButtonAux RoundIdClickable'>
      {props.roundID}
    </Link>);
  }

  const enteredPositionM = useMemo(EnteredPosition, [props.positionEntered]);
  function EnteredPosition() {
    let position = 'unknown';
    if (props.positionEntered == availablePositions.bear)
      position = (<BearIcon className='Bear' />);
    else if (props.positionEntered == availablePositions.bull)
      position = (<BullIcon className='Bull' />);
    else
      position = 'unknown';
    return (<div className='Position Entered'>
      {position}
    </div>);
  }

  const closedPositionM = useMemo(ClosedPosition, [round]);
  function ClosedPosition() {
    let position = 'unknown';
    const closedPosition = GetClosedPosition();
    if (closedPosition == 'bear')
      position = (<BearIcon className='Bear' />);
    else if (closedPosition == 'bull')
      position = (<BullIcon className='Bull' />);
    else if (closedPosition == 'tie')
      position = (<div className='Tie'>
        <BearIconHalf className='Bear' />
        <BullIconHalf className='Bull' />
      </div>);
    else if (closedPosition == 'error')
      position = (<CrossIcon className='Error' />);
    else
      position = (<QuestionmarkIcon className='Questionmark' />);
    return (<div className='Position Closed'>
      {position}
    </div>);
  }

  function GetClosedPosition() {
    let position = 'unknown';
    if (!round) return position;

    if (RoundNotEnded()) {
      return position;
    } else if (round.lockPrice == 0 || round.closePrice == 0) {
      position = 'error';
    } else if (round.lockPrice == round.closePrice) {
      position = 'tie';
    } else {
      const difference = round.lockPrice - round.closePrice;
      if (difference < 0)
        position = 'bull';
      else if (difference > 0)
        position = 'bear';
      else
        position = 'unknown';
    }
    return position;
  }

  const wageredAmountM = useMemo(WageredAmount, [props.betAmount, props.fiatID]);
  function WageredAmount() {
    if (!props.betAmount)
      return '...';
    const amountRaw = Number.parseFloat(Web3.utils.fromWei(props.betAmount, 'Ether'));
    const amount = amountRaw.toFixed(wagerDecimalsToShow);
    return FigureInChosenCurrency(amount);
  }

  const rewardStatsM = useMemo(RewardStats
    , [round, alreadyClaimed, isClaimable, isMobile, props.betAmount, props.fiatID]);
  function RewardStats() {
    if (!round || !round.rewardBaseCalAmount || !props.betAmount)
      return '...';
    let info, colorClass;
    let rewards = NormalizeNumber(props.betAmount).toFixed(wagerDecimalsToShow);
    if (alreadyClaimed || isClaimable.claimable) {
      const rewardRate = GetRewardRate();
      rewards = (rewards * rewardRate).toFixed(wagerDecimalsToShow);
      info = '+';
      colorClass = 'BullGreen';
    } else if (isClaimable.isRefund) {
      // const rewardRate = 1;
      if (isMobile)
        info = '+';
      else
        info = '[refund] +';
      colorClass = 'ThemeGold';
    } else if (RoundNotEnded()) {
      rewards = '-'; // not a bug; 'rewards', not 'info'
    } else {
      info = '-';
      colorClass = 'BearRed';
    }
    return FigureInChosenCurrency(rewards, info, colorClass);
  }

  function FigureInChosenCurrency(figure, information, colorClass) {
    if (information === undefined || information === null)
      information = '';
    if (colorClass === undefined || colorClass === null)
      colorClass = '';
    if (figure == undefined) throw new SyntaxError('Missing required argument: figure');
    let output = null;
    if (props.fiatID == 0)
      output = (<React.Fragment>
        {information + figure}
        <NetworkCurrencySymbol />
      </React.Fragment>);
    else
      output = (<React.Fragment>
        {information}
        <FiatCurrencySymbol cryptoAmount={figure} />
      </React.Fragment>);
    return (
      <div className={'WagerStatistic ' + colorClass}>
        {output}
      </div>
    );
  }

  const claimButtonM = useMemo(ClaimButton
    , [gameDetails, round, alreadyClaimed, isClaimable, props.roundID]);
  function ClaimButton() {
    let output;
    if (RoundNotEnded())
      output = null;
    else if (alreadyClaimed)
      output = (<p>Claimed</p>);
    else if (isClaimable.claimable)
      output = (<button className='ThemeButtonAux' onClick={Claim}>
        <NetworkCurrencySymbol active={true} />
        Claim
      </button>);
    else if (isClaimable.isRefund)
      output = (<button className='ThemeButtonAux' onClick={Claim}>
        <NetworkCurrencySymbol active={true} />
        Refund
      </button>);
    else
      //output = (<button className='Claim Disabled'></button>)
      output = (<p>No Rewards</p>);
    return (<div className='RoundClaim'>
      {output}
    </div>);
  }

  return (
    <tr className={'HistoryRound' + (isMobile ? ' Mobile' : '')}>
      <td colSpan='1' className='RoundID'>{roundNumberM}</td>
      <td colSpan='1'>{enteredPositionM}</td>
      <td colSpan='1'>{closedPositionM}</td>
      <td colSpan='2'>
        {wageredAmountM}
      </td>
      <td colSpan='2'>
        {rewardStatsM}
      </td >
      <td colSpan='2'>
        {claimButtonM}
      </td>
    </tr>
  );
}

export default HistoryRound;
